"use client";

import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import { IconArrowNarrowLeft, IconArrowNarrowRight, IconX } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/* -------------------------------------------------------------------------------------------------
 * Utility: cn()
 * ------------------------------------------------------------------------------------------------- */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/* -------------------------------------------------------------------------------------------------
 * Hook: useOutsideClick
 * ------------------------------------------------------------------------------------------------- */
export function useOutsideClick(ref, callback) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      callback(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, callback]);
}

/* -------------------------------------------------------------------------------------------------
 * Carousel Context
 * ------------------------------------------------------------------------------------------------- */
export const CarouselContext = createContext({
  onCardClose: () => {},
});

/* -------------------------------------------------------------------------------------------------
 * Carousel Component
 * ------------------------------------------------------------------------------------------------- */
export function Carousel({ items = [], initialScroll = 0, title = "Successfully Completed Projects" }) {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const handleCardClose = (index) => {
    if (carouselRef.current) {
      const cardWidth = isMobile() ? 200 : 300;
      const gap = isMobile() ? 4 : 8;
      const scrollPosition = (cardWidth + gap) * (index + 1);
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const isMobile = () => {
    return typeof window !== "undefined" && window.innerWidth < 768;
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose }}>
      <section className="py-8 sm:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {/* Heading */}
<div className="max-w-5xl mx-auto text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-[#741112] text-center mb-4">
          Our Delivered Projects
        </h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
  From pre-launch to sold-out, we’ve driven sales that turned projects into success stories.       
   </p>
      </div>
          <div className="relative w-full">
            <div
              className="flex w-full overflow-x-scroll overscroll-x-auto scroll-smooth py-6 sm:py-10 md:py-12 [scrollbar-width:none] hide-scrollbar"
              ref={carouselRef}
              onScroll={checkScrollability}
            >
              <style>
                {`
                  .hide-scrollbar::-webkit-scrollbar { display: none; }
                  .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                `}
              </style>
              <div
                className={cn(
                  "absolute right-0 z-[1000] h-auto w-[5%] overflow-hidden bg-gradient-to-l from-white"
                )}
              />

              <div
                className={cn(
                  "flex flex-row justify-start gap-3 sm:gap-4 md:gap-6 pl-4",
                  "mx-auto max-w-7xl"
                )}
              >
                {items.map((item, index) => (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        delay: 0.2 * index,
                        ease: "easeOut",
                        once: true,
                      },
                    }}
                    key={`card-${index}`}
                    className="rounded-3xl last:pr-[5%] sm:last:pr-[10%] md:last:pr-[20%]"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="flex justify-center gap-2 mt-4 sm:mt-0 sm:absolute sm:inset-y-0 sm:left-0 sm:right-0 sm:flex sm:justify-between sm:items-center">
              <button
                className={cn(
                  "sm:absolute sm:-left-12 md:-left-16 lg:-left-20 sm:top-1/2 sm:-translate-y-1/2 z-40",
                  "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-gray-100",
                  "disabled:opacity-50 transition-all hover:bg-gray-200"
                )}
                onClick={scrollLeft}
                disabled={!canScrollLeft}
              >
                <IconArrowNarrowLeft className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
              </button>
              <button
                className={cn(
                  "sm:absolute sm:-right-12 md:-right-16 lg:-right-20 sm:top-1/2 sm:-translate-y-1/2 z-40",
                  "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-gray-100",
                  "disabled:opacity-50 transition-all hover:bg-gray-200"
                )}
                onClick={scrollRight}
                disabled={!canScrollRight}
              >
                <IconArrowNarrowRight className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </CarouselContext.Provider>
  );
}

/* -------------------------------------------------------------------------------------------------
 * Card Component
 * ------------------------------------------------------------------------------------------------- */
export function Card({ card, index, layout = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { onCardClose } = useContext(CarouselContext);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useOutsideClick(containerRef, () => handleClose());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onCardClose(index);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-50 h-screen overflow-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 h-full w-full bg-black/80 backdrop-blur-lg"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.title}` : undefined}
              className="relative z-[60] mx-auto my-6 sm:my-8 md:my-10 h-fit max-w-4xl sm:max-w-5xl rounded-3xl bg-white p-4 sm:p-6 md:p-8 font-sans dark:bg-neutral-900"
            >
              <button
                className="sticky top-4 right-4 sm:top-6 sm:right-6 ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-black dark:bg-white"
                onClick={handleClose}
              >
                <IconX className="h-5 w-5 sm:h-6 sm:w-6 text-neutral-100 dark:text-neutral-900" />
              </button>
              <motion.p
                layoutId={layout ? `category-${card.title}` : undefined}
                className="text-xs sm:text-sm md:text-base font-medium text-black dark:text-white"
              >
                {card.category}
              </motion.p>
              <motion.p
                layoutId={layout ? `title-${card.title}` : undefined}
                className="mt-2 sm:mt-3 md:mt-4 text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-neutral-700 dark:text-white"
              >
                {card.title}
              </motion.p>
              <div className="py-6 sm:py-8 md:py-10">{card.content}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <motion.button
        layoutId={layout ? `card-${card.title}` : undefined}
        onClick={handleOpen}
        className={cn(
          "relative z-10 flex flex-col items-start justify-start overflow-hidden rounded-3xl bg-gray-100 dark:bg-neutral-900",
          "w-48 h-60 sm:w-56 sm:h-72 md:w-64 md:h-80 lg:w-72 lg:h-96"
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-full bg-gradient-to-b from-black/50 via-transparent to-transparent" />
        <div className="relative z-40 p-3 sm:p-4 md:p-6">
          <motion.p
            layoutId={layout ? `category-${card.category}` : undefined}
            className="text-left font-sans text-xs sm:text-sm font-medium text-white"
          >
            {card.category}
          </motion.p>
          <motion.p
            layoutId={layout ? `title-${card.title}` : undefined}
            className="mt-1 sm:mt-2 max-w-xs text-left font-sans text-sm sm:text-base md:text-lg font-semibold text-white"
          >
            {card.title}
          </motion.p>
        </div>
        <BlurImage
          src={card.src}
          alt={card.title}
          className="absolute inset-0 z-10 object-cover"
        />
      </motion.button>
    </>
  );
}

/* -------------------------------------------------------------------------------------------------
 * BlurImage Component
 * ------------------------------------------------------------------------------------------------- */
export function BlurImage({ height, width, src, className, alt, ...rest }) {
  const [isLoading, setLoading] = useState(true);
  return (
    <img
      className={cn(
        "h-full w-full transition duration-300",
        isLoading ? "blur-sm" : "blur-0",
        className
      )}
      onLoad={() => setLoading(false)}
      src={src}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      alt={alt || "Background of a beautiful view"}
      {...rest}
    />
  );
}